import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Container,
  Stack,
  useTheme,
  useMediaQuery,
  Grid,
  TextField,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PeopleIcon from "@mui/icons-material/People";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import BuildIcon from "@mui/icons-material/Build";
import { authenticatedFetch } from "../../utils/api";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface MonthSummary {
  year: number;
  month: number;
  month_name: string;
  revenue: number;
  expenses: number;
  profit: number;
  has_expense_record: boolean;
  expense_id?: number;
}

interface InvoiceDetail {
  id: number;
  invoice_number: string;
  company_name: string;
  date: string;
  weight: number;
  subtotal: number;
  total_amount: number;
}

interface UninvoicedDelivery {
  id: number;
  company_name: string;
  program_id: number;
  date: string;
  weight: number;
  estimated_amount: number;
}

interface AdditionalSalary {
  id?: number;
  employee_id: number;
  employee_name?: string;
  amount: number;
  salary_type: string;
  notes?: string;
  _destroy?: boolean;
}

interface ArbitraryExpense {
  id?: number;
  description: string;
  amount: number;
  _destroy?: boolean;
}

interface EmployeeWithSalary {
  id: number;
  name: string;
  salary_type: string;
  monthly_salary: number;
  pay_per_shift: number;
  shift_count: number;
  calculated_salary: number;
}

interface MonthDetail {
  year: number;
  month: number;
  month_name: string;
  default_rate: number;
  org_rate: number;
  revenue: {
    invoiced: number;
    estimated: number;
    total: number;
    uninvoiced_weight: number;
    default_rate: number;
    invoices: InvoiceDetail[];
    uninvoiced_deliveries: UninvoicedDelivery[];
  };
  expenses: {
    id?: number;
    electric_bill: number;
    needles_cost: number;
    repair_cost: number;
    default_rate: number;
    base_expenses: number;
    total_salaries: number;
    total: number;
    additional_salaries: AdditionalSalary[];
    arbitrary_expenses: ArbitraryExpense[];
  };
  employees: EmployeeWithSalary[];
  profit: number;
}

export default function Finance() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { translations: t } = useTranslation();

  const [months, setMonths] = useState<MonthSummary[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<{
    year: number;
    month: number;
  } | null>(null);
  const [monthDetail, setMonthDetail] = useState<MonthDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  // Form state for expenses
  const [defaultRate, setDefaultRate] = useState<string>("100");
  const [electricBill, setElectricBill] = useState<string>("0");
  const [needlesCost, setNeedlesCost] = useState<string>("0");
  const [repairCost, setRepairCost] = useState<string>("0");
  const [additionalSalaries, setAdditionalSalaries] = useState<AdditionalSalary[]>([]);
  const [arbitraryExpenses, setArbitraryExpenses] = useState<ArbitraryExpense[]>([]);

  // Dialog state for adding additional salary (bonus, overtime)
  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [newSalary, setNewSalary] = useState<AdditionalSalary>({
    employee_id: 0,
    amount: 0,
    salary_type: "bonus",
    notes: "",
  });

  useEffect(() => {
    fetchMonths();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      fetchMonthDetail(selectedMonth.year, selectedMonth.month);
    }
  }, [selectedMonth]);

  const fetchMonths = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/finance/months`);
      if (response.status === 401) {
        navigate("/login");
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch finance data");
      }
      const data = await response.json();
      setMonths(data);

      // Auto-select current month if available, otherwise first month
      const now = new Date();
      const currentMonth = data.find(
        (m: MonthSummary) => m.year === now.getFullYear() && m.month === now.getMonth() + 1
      );
      if (currentMonth) {
        setSelectedMonth({ year: currentMonth.year, month: currentMonth.month });
      } else if (data.length > 0) {
        setSelectedMonth({ year: data[0].year, month: data[0].month });
      } else {
        // No existing months, select current month
        setSelectedMonth({ year: now.getFullYear(), month: now.getMonth() + 1 });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthDetail = async (year: number, month: number) => {
    setDetailLoading(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/finance/${year}/${month}`
      );
      if (response.status === 401) {
        navigate("/login");
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch month details");
      }
      const data: MonthDetail = await response.json();
      setMonthDetail(data);

      // Populate form with existing data
      // Use org_rate as fallback if default_rate is not set
      setDefaultRate(data.default_rate?.toString() || data.org_rate?.toString() || "100");
      setElectricBill(data.expenses.electric_bill.toString());
      setNeedlesCost(data.expenses.needles_cost.toString());
      setRepairCost(data.expenses.repair_cost.toString());
      setAdditionalSalaries(data.expenses.additional_salaries || []);
      setArbitraryExpenses(data.expenses.arbitrary_expenses || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveExpenses = async () => {
    if (!selectedMonth || !monthDetail) return;

    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      // Prepare additional salary attributes
      const salaryAttributes = additionalSalaries.map((s) => ({
        id: s.id,
        employee_id: s.employee_id,
        amount: s.amount,
        salary_type: s.salary_type,
        notes: s.notes,
        _destroy: s._destroy,
      }));

      // Prepare arbitrary expense attributes
      const arbitraryExpenseAttributes = arbitraryExpenses.map((e) => ({
        id: e.id,
        description: e.description,
        amount: e.amount,
        _destroy: e._destroy,
      }));

      const response = await authenticatedFetch(
        `${API_URL}/finance/${selectedMonth.year}/${selectedMonth.month}`,
        {
          method: "POST",
          body: JSON.stringify({
            monthly_expense: {
              default_rate: parseFloat(defaultRate) || 100,
              electric_bill: parseFloat(electricBill) || 0,
              needles_cost: parseFloat(needlesCost) || 0,
              repair_cost: parseFloat(repairCost) || 0,
              employee_monthly_salaries_attributes: salaryAttributes,
              arbitrary_expenses_attributes: arbitraryExpenseAttributes,
            },
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      setSuccessMessage(t.finance?.saved || "Expenses saved successfully");

      // Refresh data
      await fetchMonths();
      await fetchMonthDetail(selectedMonth.year, selectedMonth.month);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save expenses");
    } finally {
      setSaving(false);
    }
  };

  const handleAddSalary = () => {
    if (newSalary.employee_id === 0 || newSalary.amount <= 0) return;

    const employee = monthDetail?.employees.find(
      (e) => e.id === newSalary.employee_id
    );

    setAdditionalSalaries([
      ...additionalSalaries,
      {
        ...newSalary,
        employee_name: employee?.name,
      },
    ]);

    // Reset dialog
    setNewSalary({
      employee_id: 0,
      amount: 0,
      salary_type: "bonus",
      notes: "",
    });
    setSalaryDialogOpen(false);
  };

  const handleRemoveSalary = (index: number) => {
    const salary = additionalSalaries[index];
    if (salary.id) {
      // Mark for deletion if it exists in DB
      setAdditionalSalaries(
        additionalSalaries.map((s, i) => (i === index ? { ...s, _destroy: true } : s))
      );
    } else {
      // Just remove from array if it's new
      setAdditionalSalaries(additionalSalaries.filter((_, i) => i !== index));
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getMonthCards = () => {
    const now = new Date();
    const cards: { year: number; month: number; monthName: string }[] = [];

    // Start from December 2025 (when data began)
    const startDate = new Date(2025, 11, 1); // December 2025
    const currentDate = new Date(startDate);

    // Generate months from December 2025 to current month
    while (currentDate <= now) {
      cards.push({
        year: currentDate.getFullYear(),
        month: currentDate.getMonth() + 1,
        monthName: currentDate.toLocaleDateString("en-US", { month: "short" }),
      });
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return cards;
  };

  const getMonthData = (year: number, month: number) => {
    return months.find((m) => m.year === year && m.month === month);
  };

  const calculateTotalExpenses = () => {
    const base =
      (parseFloat(electricBill) || 0) +
      (parseFloat(needlesCost) || 0) +
      (parseFloat(repairCost) || 0);
    
    // Add auto-calculated employee salaries
    const employeeSalaries = monthDetail?.employees?.reduce(
      (sum, emp) => sum + emp.calculated_salary, 0
    ) || 0;
    
    // Add additional salaries (bonus, overtime)
    const additionalTotal = additionalSalaries
      .filter((s) => !s._destroy)
      .reduce((sum, s) => sum + (s.amount || 0), 0);
    
    // Add arbitrary expenses
    const arbitraryTotal = arbitraryExpenses
      .filter((e) => !e._destroy)
      .reduce((sum, e) => sum + (e.amount || 0), 0);
    
    return base + employeeSalaries + additionalTotal + arbitraryTotal;
  };

  const calculateProfit = () => {
    if (!monthDetail) return 0;
    return monthDetail.revenue.total - calculateTotalExpenses();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  const monthCards = getMonthCards();

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Month Cards */}
      <Box
        sx={{
          overflowX: "auto",
          pb: 2,
          mb: 2,
          "&::-webkit-scrollbar": {
            height: 6,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.divider,
            borderRadius: 3,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ minWidth: "max-content", py: 1 }}
        >
          {monthCards.map((card) => {
            const data = getMonthData(card.year, card.month);
            const isSelected =
              selectedMonth?.year === card.year &&
              selectedMonth?.month === card.month;
            const profit = data ? data.profit : 0;
            const isPositive = profit >= 0;

            return (
              <Card
                key={`${card.year}-${card.month}`}
                onClick={() =>
                  setSelectedMonth({ year: card.year, month: card.month })
                }
                sx={{
                  minWidth: isSmallMobile ? 80 : 100,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  border: isSelected
                    ? `2px solid ${theme.palette.primary.main}`
                    : "2px solid transparent",
                  bgcolor: isSelected
                    ? theme.palette.action.selected
                    : "background.paper",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent sx={{ p: isSmallMobile ? 1 : 1.5, "&:last-child": { pb: isSmallMobile ? 1 : 1.5 } }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    textAlign="center"
                  >
                    {card.monthName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    textAlign="center"
                  >
                    {card.year}
                  </Typography>
                  {data && (
                    <Typography
                      variant="caption"
                      display="block"
                      textAlign="center"
                      fontWeight="bold"
                      color={isPositive ? "success.main" : "error.main"}
                      sx={{ mt: 0.5 }}
                    >
                      {isPositive ? "+" : ""}
                      {formatCurrency(profit)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      {/* Month Detail */}
      {detailLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <CircularProgress />
        </Box>
      ) : monthDetail ? (
        <Box>
          {/* Summary Cards */}
          <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 3 }}>
            <Grid item xs={4} sm={4}>
              <Card
                sx={{
                  bgcolor: "success.light",
                  color: "success.contrastText",
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <TrendingUpIcon sx={{ fontSize: { xs: 16, sm: 24 } }} />
                    <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {t.finance?.revenue || "Revenue"}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h5" 
                    fontWeight="bold" 
                    sx={{ 
                      mt: 0.5, 
                      fontSize: { xs: '0.9rem', sm: '1.25rem', md: '1.5rem' } 
                    }}
                  >
                    {formatCurrency(monthDetail.revenue.total)}
                  </Typography>
                  {!isSmallMobile && (
                    <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                      {t.finance?.invoiced || "Invoiced"}:{" "}
                      {formatCurrency(monthDetail.revenue.invoiced)} |{" "}
                      {t.finance?.estimated || "Est"}:{" "}
                      {formatCurrency(monthDetail.revenue.estimated)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4} sm={4}>
              <Card
                sx={{
                  bgcolor: "error.light",
                  color: "error.contrastText",
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <TrendingDownIcon sx={{ fontSize: { xs: 16, sm: 24 } }} />
                    <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {t.finance?.expenses || "Expenses"}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h5" 
                    fontWeight="bold" 
                    sx={{ 
                      mt: 0.5, 
                      fontSize: { xs: '0.9rem', sm: '1.25rem', md: '1.5rem' } 
                    }}
                  >
                    {formatCurrency(monthDetail.expenses.total)}
                  </Typography>
                  {!isSmallMobile && (
                    <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                      {t.finance?.salaries || "Salaries"}:{" "}
                      {formatCurrency(monthDetail.expenses.total_salaries)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4} sm={4}>
              <Card
                sx={{
                  bgcolor:
                    monthDetail.profit >= 0
                      ? "primary.main"
                      : "warning.main",
                  color: "primary.contrastText",
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <AccountBalanceWalletIcon sx={{ fontSize: { xs: 16, sm: 24 } }} />
                    <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      {t.finance?.profit || "Profit"}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h5" 
                    fontWeight="bold" 
                    sx={{ 
                      mt: 0.5, 
                      fontSize: { xs: '0.9rem', sm: '1.25rem', md: '1.5rem' } 
                    }}
                  >
                    {formatCurrency(monthDetail.profit)}
                  </Typography>
                  {!isSmallMobile && (
                    <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                      {monthDetail.month_name} {monthDetail.year}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Revenue Details */}
          <Accordion defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <ReceiptIcon color="success" />
                <Typography variant="h6">
                  {t.finance?.revenueDetails || "Revenue Details"}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {/* Rate Input */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  label={t.finance?.ratePerKg || "Rate per kg (for uninvoiced)"}
                  type="number"
                  value={defaultRate}
                  onChange={(e) => setDefaultRate(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    endAdornment: <InputAdornment position="end">/kg</InputAdornment>,
                  }}
                  sx={{ width: { xs: '100%', sm: 200 } }}
                />
              </Box>

              {/* Invoices */}
              {monthDetail.revenue.invoices.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t.finance?.invoicedRevenue || "Invoiced Revenue"} (
                    {formatCurrency(monthDetail.revenue.invoiced)})
                  </Typography>
                  {isSmallMobile ? (
                    // Mobile card-based layout for invoices
                    <Stack spacing={1}>
                      {monthDetail.revenue.invoices.map((inv) => (
                        <Paper 
                          key={inv.id} 
                          variant="outlined" 
                          sx={{ p: 1.5, cursor: 'pointer' }}
                          onClick={() => navigate(`/invoices/${inv.id}`)}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Chip
                              label={inv.invoice_number}
                              size="small"
                              color="primary"
                            />
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {formatCurrency(inv.subtotal)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {inv.company_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {inv.weight.toFixed(2)} kg
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t.invoices?.invoiceNumber || "Invoice #"}</TableCell>
                            <TableCell>{t.finance?.company || "Company"}</TableCell>
                            <TableCell align="right">{t.finance?.weight || "Weight"}</TableCell>
                            <TableCell align="right">{t.finance?.amount || "Amount"}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {monthDetail.revenue.invoices.map((inv) => (
                            <TableRow key={inv.id}>
                              <TableCell>
                                <Chip
                                  label={inv.invoice_number}
                                  size="small"
                                  color="primary"
                                  onClick={() => navigate(`/invoices/${inv.id}`)}
                                />
                              </TableCell>
                              <TableCell>{inv.company_name}</TableCell>
                              <TableCell align="right">
                                {inv.weight.toFixed(2)} kg
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(inv.subtotal)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* Uninvoiced Deliveries */}
              {monthDetail.revenue.uninvoiced_deliveries.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t.finance?.estimatedRevenue || "Estimated Revenue"} (
                    {formatCurrency(monthDetail.revenue.estimated)}) -{" "}
                    {t.finance?.rate || "Rate"}: ₹{monthDetail.revenue.default_rate}/kg
                  </Typography>
                  {isSmallMobile ? (
                    // Mobile card-based layout for uninvoiced deliveries
                    <Stack spacing={1}>
                      {monthDetail.revenue.uninvoiced_deliveries.map((del) => (
                        <Paper 
                          key={del.id} 
                          variant="outlined" 
                          sx={{ p: 1.5, cursor: 'pointer' }}
                          onClick={() => navigate(`/deliveries/${del.id}`)}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Chip
                              label={`#${del.id}`}
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="body2" fontWeight="bold" color="warning.main">
                              {formatCurrency(del.estimated_amount)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {del.company_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {del.weight.toFixed(2)} kg
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t.finance?.delivery || "Delivery"}</TableCell>
                            <TableCell>{t.finance?.company || "Company"}</TableCell>
                            <TableCell align="right">{t.finance?.weight || "Weight"}</TableCell>
                            <TableCell align="right">{t.finance?.estimated || "Estimated"}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {monthDetail.revenue.uninvoiced_deliveries.map((del) => (
                            <TableRow key={del.id}>
                              <TableCell>
                                <Chip
                                  label={`#${del.id}`}
                                  size="small"
                                  variant="outlined"
                                  onClick={() => navigate(`/deliveries/${del.id}`)}
                                />
                              </TableCell>
                              <TableCell>{del.company_name}</TableCell>
                              <TableCell align="right">
                                {del.weight.toFixed(2)} kg
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(del.estimated_amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {monthDetail.revenue.invoices.length === 0 &&
                monthDetail.revenue.uninvoiced_deliveries.length === 0 && (
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    {t.finance?.noRevenueData || "No revenue data for this month"}
                  </Typography>
                )}
            </AccordionDetails>
          </Accordion>

          {/* Expenses Form */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Box display="flex" alignItems="center" gap={1}>
                  <TrendingDownIcon color="error" />
                  {t.finance?.enterExpenses || "Enter Expenses"}
                </Box>
              </Typography>

              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 1 }}>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    label={t.finance?.electricBill || "Electric Bill"}
                    type="number"
                    value={electricBill}
                    onChange={(e) => setElectricBill(e.target.value)}
                    size={isSmallMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <ElectricBoltIcon
                          color="action"
                          sx={{ mr: 0.5, fontSize: { xs: 18, sm: 24 } }}
                        />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    label={t.finance?.needlesCost || "Needles Cost"}
                    type="number"
                    value={needlesCost}
                    onChange={(e) => setNeedlesCost(e.target.value)}
                    size={isSmallMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 4, fontSize: isSmallMobile ? 14 : 18 }}>🪡</span>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t.finance?.repairCost || "Repair Cost"}
                    type="number"
                    value={repairCost}
                    onChange={(e) => setRepairCost(e.target.value)}
                    size={isSmallMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <BuildIcon color="action" sx={{ mr: 0.5, fontSize: { xs: 18, sm: 24 } }} />
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {/* Employee Salaries (Auto-calculated) */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PeopleIcon color="primary" />
                    {t.finance?.employeeSalaries || "Employee Salaries"} ({t.finance?.autoCalculated || "Auto-calculated"})
                  </Box>
                </Typography>

                {monthDetail.employees && monthDetail.employees.length > 0 ? (
                  isSmallMobile ? (
                    // Mobile card-based layout for employee salaries
                    <Box sx={{ mb: 2 }}>
                      <Stack spacing={1}>
                        {monthDetail.employees
                          .filter((emp) => emp.calculated_salary > 0)
                          .map((emp) => (
                            <Paper 
                              key={emp.id} 
                              variant="outlined" 
                              sx={{ p: 1.5, cursor: 'pointer' }}
                              onClick={() => navigate(`/employees/${emp.id}`)}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                                    {emp.name}
                                  </Typography>
                                  <Chip
                                    label={emp.salary_type === 'monthly' ? (t.finance?.monthly || 'Monthly') : (t.finance?.shiftBased || 'Shift Based')}
                                    size="small"
                                    variant="outlined"
                                    color={emp.salary_type === 'monthly' ? 'primary' : 'secondary'}
                                    sx={{ mt: 0.5 }}
                                  />
                                </Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {formatCurrency(emp.calculated_salary)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                {emp.salary_type === 'shift_based' && (
                                  <Typography variant="caption" color="text.secondary">
                                    {emp.shift_count} shifts × {formatCurrency(emp.pay_per_shift)}
                                  </Typography>
                                )}
                                {emp.salary_type === 'monthly' && (
                                  <Typography variant="caption" color="text.secondary">
                                    {t.finance?.payRate || "Pay Rate"}: {formatCurrency(emp.monthly_salary)}
                                  </Typography>
                                )}
                              </Box>
                            </Paper>
                          ))}
                      </Stack>
                      <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: 'action.selected' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {t.total || "Total"}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(
                              monthDetail.employees.reduce((sum, emp) => sum + emp.calculated_salary, 0)
                            )}
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t.finance?.employee || "Employee"}</TableCell>
                            <TableCell>{t.finance?.type || "Type"}</TableCell>
                            <TableCell align="center">{t.finance?.shifts || "Shifts"}</TableCell>
                            <TableCell align="right">{t.finance?.payRate || "Pay Rate"}</TableCell>
                            <TableCell align="right">{t.finance?.salary || "Salary"}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {monthDetail.employees
                            .filter((emp) => emp.calculated_salary > 0)
                            .map((emp) => (
                              <TableRow key={emp.id}>
                                <TableCell>
                                  <Typography
                                    component="span"
                                    sx={{
                                      cursor: 'pointer',
                                      color: 'primary.main',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                    onClick={() => navigate(`/employees/${emp.id}`)}
                                  >
                                    {emp.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={emp.salary_type === 'monthly' ? (t.finance?.monthly || 'Monthly') : (t.finance?.shiftBased || 'Shift Based')}
                                    size="small"
                                    variant="outlined"
                                    color={emp.salary_type === 'monthly' ? 'primary' : 'secondary'}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {emp.salary_type === 'shift_based' ? emp.shift_count : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {emp.salary_type === 'monthly' 
                                    ? formatCurrency(emp.monthly_salary)
                                    : `${formatCurrency(emp.pay_per_shift)}/shift`
                                  }
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  {formatCurrency(emp.calculated_salary)}
                                </TableCell>
                              </TableRow>
                            ))}
                          <TableRow>
                            <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>
                              {t.total || "Total"}:
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(
                                monthDetail.employees.reduce((sum, emp) => sum + emp.calculated_salary, 0)
                              )}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )
                ) : (
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    {t.finance?.noEmployees || "No employees configured"}
                  </Typography>
                )}
              </Box>

              {/* Additional Salaries (Bonus, Overtime) */}
              <Box sx={{ mt: 3 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="subtitle1">
                    <Box display="flex" alignItems="center" gap={1}>
                      <AddIcon color="secondary" />
                      {t.finance?.additionalPayments || "Additional Payments"} ({t.finance?.bonusOvertime || "Bonus/Overtime"})
                    </Box>
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setSalaryDialogOpen(true)}
                  >
                    {t.add || "Add"}
                  </Button>
                </Box>

                {additionalSalaries.filter((s) => !s._destroy).length > 0 ? (
                  isSmallMobile ? (
                    // Mobile card-based layout for additional salaries
                    <Stack spacing={1}>
                      {additionalSalaries
                        .map((salary, index) => ({ salary, index }))
                        .filter(({ salary }) => !salary._destroy)
                        .map(({ salary, index }) => (
                          <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight="bold" 
                                  color="primary.main"
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() => navigate(`/employees/${salary.employee_id}`)}
                                >
                                  {salary.employee_name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Chip
                                    label={salary.salary_type}
                                    size="small"
                                    variant="outlined"
                                    color={
                                      salary.salary_type === "bonus"
                                        ? "success"
                                        : salary.salary_type === "overtime"
                                        ? "warning"
                                        : "default"
                                    }
                                  />
                                  <Typography variant="body2" fontWeight="bold">
                                    {formatCurrency(salary.amount)}
                                  </Typography>
                                </Box>
                                {salary.notes && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {salary.notes}
                                  </Typography>
                                )}
                              </Box>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveSalary(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Paper>
                        ))}
                    </Stack>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{t.finance?.employee || "Employee"}</TableCell>
                            <TableCell>{t.finance?.type || "Type"}</TableCell>
                            <TableCell align="right">{t.finance?.amount || "Amount"}</TableCell>
                            <TableCell>{t.finance?.notes || "Notes"}</TableCell>
                            <TableCell align="center">{t.actions}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {additionalSalaries
                            .map((salary, index) => ({ salary, index }))
                            .filter(({ salary }) => !salary._destroy)
                            .map(({ salary, index }) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <Typography
                                    component="span"
                                    sx={{
                                      cursor: 'pointer',
                                      color: 'primary.main',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                    onClick={() => navigate(`/employees/${salary.employee_id}`)}
                                  >
                                    {salary.employee_name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={salary.salary_type}
                                    size="small"
                                    variant="outlined"
                                    color={
                                      salary.salary_type === "bonus"
                                        ? "success"
                                        : salary.salary_type === "overtime"
                                        ? "warning"
                                        : "default"
                                    }
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(salary.amount)}
                                </TableCell>
                                <TableCell>{salary.notes || "-"}</TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveSalary(index)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )
                ) : (
                  <Typography
                    color="text.secondary"
                    textAlign="center"
                    py={2}
                  >
                    {t.finance?.noAdditionalPayments || "No additional payments"}
                  </Typography>
                )}
              </Box>

              {/* Arbitrary Expenses */}
              <Box sx={{ mt: 3 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="subtitle1">
                    <Box display="flex" alignItems="center" gap={1}>
                      <ReceiptIcon color="warning" />
                      {t.finance?.otherExpenses || "Other Expenses"}
                    </Box>
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setArbitraryExpenses([
                        ...arbitraryExpenses,
                        { description: "", amount: 0 },
                      ]);
                    }}
                  >
                    {t.add || "Add"}
                  </Button>
                </Box>

                {arbitraryExpenses.filter((e) => !e._destroy).length > 0 ? (
                  <Stack spacing={2}>
                    {arbitraryExpenses
                      .map((expense, index) => ({ expense, index }))
                      .filter(({ expense }) => !expense._destroy)
                      .map(({ expense, index }) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 2 },
                            alignItems: { xs: 'stretch', sm: 'flex-start' },
                          }}
                        >
                          <TextField
                            label={t.finance?.description || "Description"}
                            value={expense.description}
                            onChange={(e) => {
                              const updated = [...arbitraryExpenses];
                              updated[index] = {
                                ...updated[index],
                                description: e.target.value,
                              };
                              setArbitraryExpenses(updated);
                            }}
                            sx={{ flex: 2 }}
                            size="small"
                            fullWidth={isSmallMobile}
                          />
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              label={t.finance?.amount || "Amount"}
                              type="number"
                              value={expense.amount || ""}
                              onChange={(e) => {
                                const updated = [...arbitraryExpenses];
                                updated[index] = {
                                  ...updated[index],
                                  amount: parseFloat(e.target.value) || 0,
                                };
                                setArbitraryExpenses(updated);
                              }}
                              sx={{ flex: 1, minWidth: { xs: 0, sm: 120 } }}
                              size="small"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">₹</InputAdornment>
                                ),
                              }}
                            />
                            <IconButton
                              color="error"
                              onClick={() => {
                                if (expense.id) {
                                  // Mark for deletion if it exists in DB
                                  const updated = [...arbitraryExpenses];
                                  updated[index] = { ...updated[index], _destroy: true };
                                  setArbitraryExpenses(updated);
                                } else {
                                  // Just remove from array if it's new
                                  setArbitraryExpenses(
                                    arbitraryExpenses.filter((_, i) => i !== index)
                                  );
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                  </Stack>
                ) : (
                  <Typography
                    color="text.secondary"
                    textAlign="center"
                    py={2}
                  >
                    {t.finance?.noOtherExpenses || "No other expenses added"}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Save Button */}
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSaveExpenses}
                  disabled={saving}
                  size="large"
                >
                  {saving
                    ? t.loading
                    : t.finance?.saveExpenses || "Save Expenses"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Typography color="text.secondary" textAlign="center" py={4}>
          {t.finance?.selectMonth || "Select a month to view details"}
        </Typography>
      )}

      {/* Add Additional Salary Dialog */}
      <Dialog
        open={salaryDialogOpen}
        onClose={() => setSalaryDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t.finance?.addPayment || "Add Payment"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t.finance?.employee || "Employee"}</InputLabel>
              <Select
                value={newSalary.employee_id}
                label={t.finance?.employee || "Employee"}
                onChange={(e) =>
                  setNewSalary({ ...newSalary, employee_id: e.target.value as number })
                }
              >
                <MenuItem value={0} disabled>
                  {t.finance?.selectEmployee || "Select Employee"}
                </MenuItem>
                {monthDetail?.employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t.finance?.paymentType || "Payment Type"}</InputLabel>
              <Select
                value={newSalary.salary_type}
                label={t.finance?.paymentType || "Payment Type"}
                onChange={(e) =>
                  setNewSalary({ ...newSalary, salary_type: e.target.value })
                }
              >
                <MenuItem value="bonus">{t.finance?.bonus || "Bonus"}</MenuItem>
                <MenuItem value="overtime">{t.finance?.overtime || "Overtime"}</MenuItem>
                <MenuItem value="other">{t.finance?.other || "Other"}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={t.finance?.amount || "Amount"}
              type="number"
              value={newSalary.amount || ""}
              onChange={(e) =>
                setNewSalary({
                  ...newSalary,
                  amount: parseFloat(e.target.value) || 0,
                })
              }
            />

            <TextField
              fullWidth
              label={t.finance?.notes || "Notes"}
              value={newSalary.notes || ""}
              onChange={(e) =>
                setNewSalary({ ...newSalary, notes: e.target.value })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalaryDialogOpen(false)}>{t.cancel}</Button>
          <Button
            variant="contained"
            onClick={handleAddSalary}
            disabled={newSalary.employee_id === 0 || newSalary.amount <= 0}
          >
            {t.add}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
